<template>
  <section class="streaming-program" ref="container">
    <app-header :showSocialLinks="false"></app-header>
    <MaintenanceNotice v-if="isMaintenance" :statusData="maintenanceStatusData" :maintenanceType="MaintenanceType.STREAMING" />
    <div class="program-wrapper" v-if="!isLoading && !isMaintenance">
      <div class="video-area">
        <div class="program-info" v-show="isDefaultMode">
          <!-- 節目概述 -->
          <div v-show="!showMoreInfo" class="program-summary">
            <program-summary
              :showMoreInfo="showMoreInfo"
              :streamingInfo="streamingInfo"
              :infoExpand="infoExpand"
              @toggleShowMore="toggleShowMore"
              @toggleShowChatRoom="toggleShowChatRoom"
            >
            </program-summary>
          </div>
          <!-- 節目詳細說明 -->
          <div v-show="showMoreInfo">
            <program-details @toggleShowMore="toggleShowMore" :streamingInfo="streamingInfo"> </program-details>
          </div>
        </div>
        <div class="video-container" style="width: 100%; height: 100%" v-if="isPlayerVisible">
          <player-page
            v-if="streamingInfo != null && !showCheckKickPCAlert"
            :streamingInfo="streamingInfo"
            @video-seeked="onVideoSeeked"
            @video-seeking="onVideoSeeking"
            @video-paused="onVideoPaused"
            @video-played="onVideoPlayed"
            @refresh-streaming-info="fetchStreamingInfo"
            @ticket-check-failed-fallback="handleTicketCheckFailedFallback"
            style="width: inherit; height: inherit"
          >
          </player-page>
        </div>
        <!-- Unity WebGL 視窗 -->
        <WebGLPage v-if="isUnityVisible" :streamingInfo="streamingInfo" />
      </div>

      <!-- 聊天室 -->
      <chat-room
        v-show="showChatRoom"
        v-if="streamingInfo && (streamingType != 'replay' || isChatRoomVisible)"
        :cid="chatRoomId"
        :scs="userItemsScs"
        :stickers="userItemsStickers"
        :getUserItems="getUserItems"
        :streamingInfo="streamingInfo"
        :productId="productId"
        @toggle-showChatRoom="toggleShowChatRoom"
        @toggle-showLoginModal="toggleShowLoginModal"
        ref="chatRoomRef"
      >
      </chat-room>

      <!-- 說明Title_Mobile -->
      <div class="title-info" v-if="isDescriptionMode_Mobile">
        <p>{{ $t('streamingVideo.key001') }}</p>
        <i class="bi bi-x leave-icon" @click="toggleShowMore"></i>
      </div>

      <!-- 詳細說明_Mobile -->
      <div v-show="isDescriptionMode_Mobile" class="modal-overlay-mobile">
        <program-details @toggleShowMore="toggleShowMore" :streamingInfo="streamingInfo"></program-details>
      </div>
    </div>

    <section class="member-login streaming-chatRoom-Login" v-if="showLoginModal">
      <div class="mask" @click="closeLoginComponent"></div>
      <div class="login-component">
        <login @next="afterLogin" :nextPath="`streaming/${roomId}`" :directions="$t('streamingVideo.key012')"></login>
      </div>
    </section>
    <Loading v-show="isLoading" />
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, provide, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getStreamingInfo, getUserItems as getUserItemsApi, kickPCUser, checkPCLoginStatus, getStreamingFallbackTicketProductId } from '@/api/streamingService.js';
import { checkLogin, getUserInfo } from '@/api/apiService.js';
import AppHeader from '@/components/Header.vue';
import ProgramSummary from '@/pages/programVideo/programInfo/ProgramSummary.vue';
import ProgramDetails from '@/pages/programVideo/programInfo/ProgramDetails.vue';
import PlayerPage from '@/pages/programVideo/video/PlayerPage.vue';
import WebGLPage from '@/pages/programVideo/UnityWebGL/WebGLPage.vue';
import ChatRoom from '@/pages/programVideo/chatRoom/ChatRoom.vue';
import Login from '@/pages/login/Login.vue';
import { showCustomAlert, AlertType } from '@/utils/alertController';
import { HttpStatusCode } from 'axios';
import Loading from '@/components/Loading.vue';
import MaintenanceNotice, { MaintenanceType } from '@/components/MaintenanceNotice.vue';
import { CUSTOM_ERROR_CODE } from '@/utils/validation.js';

const store = useStore();
const router = useRouter();
const UPDATE_MARQUEES_INTERVAL = 10000;
const CHECK_LOGIN_INTERVAL = 10000;
const CHECK_STREAMING_INTERVAL = 30000;

provide('isListeningNext', true);

const isMaintenance = ref(false);
const isCheckingLogin = ref(false);
const checkLoginInterval = ref(null);
const updateMarqueesInterval = ref(null);
const checkStreamingInterval = ref(null);
const streamingInfo = ref(null);
const showLoginModal = ref(false);
const isAlertShowed = ref(false);
const infoExpand = ref(false);
const showCheckKickPCAlert = ref(false);
const isLoading = ref(false);

const isWebGLActive = ref(); // 直播間是否支援 WebGL
const isPlayerVisible = ref(true); // 是否生成直播畫面
const isUnityVisible = ref(false); // 是否生成 Unity 畫面
const isChatRoomVisible = ref(false); // 是否顯示聊天室
const streamingType = ref(); // 頁面類型 live 直播 replay 回放
let switchTimer = null;

const userItemsScs = ref([]);
const userItemsStickers = ref([]);
const userInternalJwt = ref('');

const productId = ref('');
const chatRoomId = ref('');
const showChatRoom = ref(true);
const showMoreInfo = ref(false);

const triggerLogin = ref(0);

const maintenanceStatusData = ref({});

provide('afterLoginTrigger', triggerLogin);

const currentLanguage = computed(() => store.state.currentLanguage);
const jwt = computed(() => store.state.jwt);
const isMobile = computed(() => store.state.isMobile);
const isDefaultMode = computed(() => (!showChatRoom.value && !showMoreInfo.value) || !isMobile.value);
const isDescriptionMode_Mobile = computed(() => showMoreInfo.value && isMobile.value);

const roomId = computed(() => router.currentRoute.value.params.roomId);

// workaround for router.currentRoute.value.params.roomId is not updated
watch(roomId, () => {
  window.location.reload();
});
// 觀察如果視窗不是手機尺寸就強制顯示聊天室
watch(isMobile, () => {
  if (!isMobile.value) showChatRoom.value = true;
});
// 觀察從維護頁切回直播頁面時需要重整版型
watch(isMaintenance, (val) => {
  if (!val) {
    nextTick(() => {
      setDynamicHeight();
    });
  }
});

//Calculate Full-Screen Height for Mobile
const container = ref(null);

const setDynamicHeight = () => {
  if (container.value) {
    container.value.style.height = `${window.innerHeight}px`;
  }
};

const chatRoomRef = ref(null);
const onVideoSeeked = (newTime) => {
  if (chatRoomRef.value) {
    if (typeof chatRoomRef.value.onVideoSeeked === 'function') {
      chatRoomRef.value.onVideoSeeked(newTime);
    }
  }
};
const onVideoSeeking = () => {
  if (chatRoomRef.value) {
    if (typeof chatRoomRef.value.onVideoSeeking === 'function') {
      chatRoomRef.value.onVideoSeeking();
    }
  }
};
const onVideoPaused = () => {
  if (chatRoomRef.value) {
    if (typeof chatRoomRef.value.onVideoPaused === 'function') {
      chatRoomRef.value.onVideoPaused();
    }
  }
};
const onVideoPlayed = (newTime) => {
  if (chatRoomRef.value) {
    if (typeof chatRoomRef.value.onVideoPlayed === 'function') {
      chatRoomRef.value.onVideoPlayed(newTime);
    }
  }
};

onMounted(async () => {
  setDynamicHeight();
  window.addEventListener('resize', setDynamicHeight);

  // 顯示讀取畫面 取得資料確認合法後才隱藏讀取畫面並繪製UI介面
  isLoading.value = true;

  await fetchStreamingInfo();

  if (streamingInfo.value?.needLogin) {
    // 如果有WebGL就不在前端踢掉玩家
    if (!isWebGLActive.value) {
      const checkPCLoginStatusResponse = await checkPCLoginStatus();

      if (checkPCLoginStatusResponse.data.data.isLogin) {
        showCheckKickPCAlert.value = true;
        try {
          await showCustomAlert({
            title: 'common.alert_key003',
            message: 'streamingVideo.key031',
            type: AlertType.CONFIRM,
          });
          handleCheckKickPCAlertConfirm();
        } catch {
          handleCheckKickPCAlertCancel();
          return;
        }
      }
    }
  }

  await userInfoInit();
  showLoginModal.value = false;

  isLoading.value = false;

  updateMarqueesInterval.value = setInterval(() => {
    store.dispatch('updateMarquees', router.currentRoute.value);
  }, UPDATE_MARQUEES_INTERVAL);
});

const handleCheckKickPCAlertConfirm = async () => {
  await kickPCUser();
  showCheckKickPCAlert.value = false;
};

const handleCheckKickPCAlertCancel = () => {
  showCheckKickPCAlert.value = false;
  router.back();
};

onBeforeUnmount(() => {
  window.removeEventListener('resize', setDynamicHeight);

  removeCheckLoginInterval();

  if (updateMarqueesInterval.value) {
    clearInterval(updateMarqueesInterval.value);
    updateMarqueesInterval.value = null;
  }
  if (localStorage.getItem('beatdayJwt')) {
    localStorage.removeItem('beatdayJwt');
  }
  if (switchTimer !== null) {
    clearTimeout(switchTimer);
    switchTimer = null;
  }
  if (checkStreamingInterval.value) {
    clearInterval(checkStreamingInterval.value);
    checkStreamingInterval.value = null;
  }
});

const userInfoInit = async () => {
  // 如果已經登入, 先取得 user info & 道具 (檢查是否有填基本資訊)
  if (jwt.value) {
    await getUserInfo(jwt.value, true);
    await getUserItems();
  }
};

const removeCheckLoginInterval = () => {
  if (checkLoginInterval.value) {
    clearInterval(checkLoginInterval.value);
    checkLoginInterval.value = null;
  }
};

const fetchStreamingInfo = async () => {
  try {
    const langSetting = currentLanguage.value === 'zh_tw' ? 'zh-tw' : currentLanguage.value;
    const response = await getStreamingInfo(roomId.value, langSetting);
    const data = response.data.data;

    // 確認是否進入維修頁面
    if (data.maintenanceStatus.status == 'maintenance') {
      // 將維護頁面資料傳入維護頁面元件
      maintenanceStatusData.value = response.data.data.maintenanceStatus;
      isMaintenance.value = true;
    }

    // 確認直播是否已經結束
    if (data.endTime <= new Date()) {
      await showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key039' });
      router.push({
        path: `/${store.state.currentLanguage}`,
      });
      return;
    }

    // 確認是否進入 WebGL 畫面
    checkAndActivateWebGL(data);

    // 設定計時器 定期檢查直播間 維護狀態和WebGL狀態
    if (checkStreamingInterval.value) {
      clearInterval(checkStreamingInterval.value);
      checkStreamingInterval.value = null;
    }
    checkStreamingInterval.value = setInterval(() => {
      checkStreamingStatus();
    }, CHECK_STREAMING_INTERVAL);

    if (!data.isActive) {
      await showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key026' });
      router.replace({ path: `/${currentLanguage.value}/` });
      return;
    }
    productId.value = data.fallbackTicketProductId;
    chatRoomId.value = data.chatRoomId;
    streamingInfo.value = data;

    // 設定直播頁面類型
    streamingType.value = data.type;
    isChatRoomVisible.value = data.replayInfo.isReplayChatRoom;

    if (jwt.value) {
      // before set checkLoginInterval, remove the old interval
      removeCheckLoginInterval();
      checkLoginInterval.value = setInterval(checkUserLogin, CHECK_LOGIN_INTERVAL);
    }
  } catch (error) {
    errorReport(error);
  }
};

// 判斷 WebGL 是否該出現並運行
const checkAndActivateWebGL = (data) => {
  isWebGLActive.value = data.isWebGL;
  const now = Date.now();

  // 判斷如果 當前時間還沒進入開始時間 且 直播間有支援WebGL 時 開啟WebGL
  if (now < data.startTime && isWebGLActive.value) {
    isPlayerVisible.value = false;
    isUnityVisible.value = true;

    // 一次性定時器，在開始時間到達後切換
    scheduleUnitySwitch(data.startTime);
  } else {
    if (switchTimer !== null) {
      clearTimeout(switchTimer);
      switchTimer = null;
    }
    isPlayerVisible.value = true;
    isUnityVisible.value = false;
  }
};

// WebGL 倒數計時器
function scheduleUnitySwitch(startTime) {
  const now = Date.now();
  let diff = startTime - now;
  const MAX_TIMEOUT = 60000; // 1 分鐘

  // 若等待時間超過最大值則不設定
  if (diff > MAX_TIMEOUT) {
    return;
  }

  // 若 timer 已存在，避免重複設定
  if (switchTimer) {
    clearTimeout(switchTimer);
  }

  // 若已到直播開始時間則直接顯示直播
  if (diff <= 0) {
    isPlayerVisible.value = true; // 顯示直播
    isUnityVisible.value = false; // 關閉 WebGL
    return;
  }

  // 設定切換直播時間
  switchTimer = setTimeout(() => {
    isPlayerVisible.value = true; // 顯示直播
    isUnityVisible.value = false; // 關閉 WebGL
    switchTimer = null;
  }, diff);
}

/**
 * 不需登入才能觀看的頁面, 重複登入時, 不跳轉到登入頁面, 在原頁面 alert 後彈出 login modal
 *
 * 需要登入才能觀看的頁面, 重複登入時, 跳轉到登入頁面之後 alert
 */
const handleCompetedLogin = () => {
  if (!streamingInfo.value?.needLogin) {
    showAlert('streamingVideo.key025');

    removeCheckLoginInterval();
    showLoginModal.value = true;

    // not to redirect to login page (occurred by axios interceptor)
    router.replace({ path: `/${currentLanguage.value}/streaming/${roomId.value}` });
  } else {
    setTimeout(() => {
      showAlert('streamingVideo.key025');
    }, 500);
  }
};

const showAlert = async (message) => {
  if (!isAlertShowed.value) {
    isAlertShowed.value = true;
    await showCustomAlert({ title: 'common.alert_key003', message });
    isAlertShowed.value = false;
  }
};

const checkStreamingStatus = async () => {
  try {
    const langSetting = currentLanguage.value === 'zh_tw' ? 'zh-tw' : currentLanguage.value;
    const response = await getStreamingInfo(roomId.value, langSetting);
    const data = response.data.data;

    if (!data.isActive) {
      await showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key026' });
      router.replace({ path: `/${currentLanguage.value}/` });
      return;
    }

    streamingInfo.value = data; // 更新直播資訊 傳遞給 PlayerPage 和 WebGLPage

    // 根據 maintenanceStatus 判斷是否進入直播頁維修
    if (data.maintenanceStatus.status === 'maintenance') {
      isMaintenance.value = true;
      maintenanceStatusData.value = response.data.data.maintenanceStatus;
    } else {
      isMaintenance.value = false;
    }

    // 確認直播是否已經結束
    if (data.endTime <= new Date()) {
      await showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key039' });
      router.push({
        path: `/${store.state.currentLanguage}`,
      });
      return;
    }

    // 更新 WebGL 是否顯示
    checkAndActivateWebGL(data);
  } catch (error) {
    errorReport(error);
  }
};

const checkUserLogin = async () => {
  try {
    // 如果有WebGL就不在前端踢掉玩家
    if (isWebGLActive.value) {
      return;
    }
    if (isCheckingLogin.value) {
      return;
    }

    isCheckingLogin.value = true;
    await checkLogin();
  } catch (error) {
    if (error?.response?.status === HttpStatusCode.Unauthorized) {
      handleCompetedLogin();
    }
  } finally {
    isCheckingLogin.value = false;
  }
};

// 將後端錯誤捕捉後根據錯誤訊息決定前端行為
const errorReport = async (error) => {
  const customCode = error?.response.data.data?.customCode;
  let message = 'streamingVideo.key026';
  if (customCode === CUSTOM_ERROR_CODE.TICKET_HAS_BEEN_USED) {
    message = 'streamingVideo.key038';
  } else if (customCode === CUSTOM_ERROR_CODE.STREAMING_ROOM_LOGIN_REQUIRED) {
    router.push(`/${currentLanguage.value}/member-login/login`);
    // 轉換頁面後延遲出現 讓警告在登入介面顯示
    setTimeout(() => {
      showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key023' });
    }, 500);
    return;
  } else if (customCode === CUSTOM_ERROR_CODE.STREAMING_NOT_AVAILABLE_IN_YOUR_COUNTRY) {
    message = 'streamingVideo.key034';
  } else if (customCode === CUSTOM_ERROR_CODE.INVALID_USER_TICKETS) {
    handleTicketCheckFailedFallback();
    return;
  }
  await showCustomAlert({ title: 'common.alert_key003', message: message });
  router.replace({ path: `/${currentLanguage.value}/` });
};

provide('handleCompetedLogin', handleCompetedLogin);

const toggleShowMore = () => {
  showMoreInfo.value = !showMoreInfo.value;
};
const toggleShowLoginModal = () => {
  showLoginModal.value = !showLoginModal.value;
};
const toggleShowChatRoom = () => {
  showChatRoom.value = !showChatRoom.value;
};
const closeLoginComponent = () => {
  showLoginModal.value = false;
};
const afterLogin = () => {
  closeLoginComponent();

  getUserItems();

  isAlertShowed.value = false;
  checkLoginInterval.value = setInterval(checkUserLogin, CHECK_LOGIN_INTERVAL);

  triggerLogin.value++;
};

const getUserItems = async () => {
  if (!jwt.value) {
    return;
  }

  try {
    const response = await getUserItemsApi(jwt.value);
    const responseData = response.data.data;
    userItemsScs.value = responseData.scs || [];
    userItemsStickers.value = responseData.stickers || [];
    userInternalJwt.value = responseData.internalJwt;

    // 把 jwt 存入 LocalStorage WebGL環境需求
    localStorage.setItem('beatdayJwt', userInternalJwt.value);
  } catch (error) {
    if (error?.response?.status === HttpStatusCode.Unauthorized && typeof handleCompetedLogin === 'function') {
      handleCompetedLogin();
    }
    console.error('Error fetching get SC info:', error);
  }
};

const handleTicketCheckFailedFallback = async () => {
  const langSetting = currentLanguage.value === 'zh_tw' ? 'zh-tw' : currentLanguage.value;
  const response = await getStreamingFallbackTicketProductId(roomId.value, langSetting);
  const data = response.data.data;

  router.push({
    path: `/${currentLanguage.value}/product-info/${data}`,
    query: { source: 'streaming' },
  });
};
</script>

<style lang="scss">
@import '@/assets/styles/pages/memberCenter/streamingProgram.scss';
</style>
